import { AuthResponse } from "../interfaces/AuthResponse";
import { RootState } from "../store/store";
import { authApi } from "./authApi";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface AuthState {
  username?: string;
  token?: string;
  error?: string;
  initialized: boolean;
  forgotPasswordMessage?: string;
  resetPasswordMessage?: string;
}

const initialState: AuthState = {
  username: undefined,
  token: undefined,
  error: undefined,
  initialized: false,
  forgotPasswordMessage: undefined,
  resetPasswordMessage: undefined,
};

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<{ username: string; token: string }>) => {
      state.username = action.payload.username;
      state.token = action.payload.token;
      state.error = undefined;
      state.initialized = true;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearAuth: (state) => {
      state.username = undefined;
      state.token = undefined;
      state.error = undefined;
      state.initialized = true;
      state.forgotPasswordMessage = undefined;
      state.resetPasswordMessage = undefined;
    },
    clearMessages: (state) => {
      state.error = undefined;
      state.forgotPasswordMessage = undefined;
      state.resetPasswordMessage = undefined;
    },
    setInitialized: (state) => {
      state.initialized = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }: PayloadAction<AuthResponse>) => {
        state.username = payload.username;
        state.token = payload.token;
        state.error = undefined;
        state.initialized = true;
      })
      .addMatcher(authApi.endpoints.register.matchFulfilled, (state, { payload }: PayloadAction<AuthResponse>) => {
        state.username = payload.username;
        state.token = payload.token;
        state.error = undefined;
        state.initialized = true;
      })
      .addMatcher(authApi.endpoints.verify.matchFulfilled, (state) => {
        state.error = undefined; // Token is valid, clear any prior errors
      })
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        const error = action.error;
        const payload = action.payload;
        
        // Check multiple possible locations for the real error message
        const errorMessage = 
          (payload as any)?.data ||              // Original API error in payload
          (error as any).data ||                 // Direct string message from API  
          (payload as any)?.message ||           // Message in payload
          (error as any).data?.message ||        // JSON format if API changes
          (error as any).message ||              // Standard error format
          "Login failed";
        state.error = errorMessage;
      })
      .addMatcher(authApi.endpoints.register.matchRejected, (state, action) => {
        const error = action.error;
        const payload = action.payload;
        
        // Check multiple possible locations for the real error message
        const errorMessage = 
          (payload as any)?.data ||              // Original API error in payload
          (error as any).data ||                 // Direct string message from API  
          (payload as any)?.message ||           // Message in payload
          (error as any).data?.message ||        // JSON format if API changes
          (error as any).message ||              // Standard error format
          "Registration failed";
        state.error = errorMessage;
      })
      .addMatcher(authApi.endpoints.verify.matchRejected, (state, { error }) => {
        state.error = (error as any).data?.message || error.message || "Verification failed";
        state.username = undefined;
        state.token = undefined;
      })
      .addMatcher(authApi.endpoints.forgotPassword.matchFulfilled, (state, { payload }) => {
        state.forgotPasswordMessage = payload.message;
        state.error = undefined;
      })
      .addMatcher(authApi.endpoints.forgotPassword.matchRejected, (state, action) => {
        const error = action.error;
        const payload = action.payload;
        
        const errorMessage = 
          (payload as any)?.data ||
          (error as any).data ||
          (payload as any)?.message ||
          (error as any).data?.message ||
          (error as any).message ||
          "Failed to send reset email";
        state.error = errorMessage;
        state.forgotPasswordMessage = undefined;
      })
      .addMatcher(authApi.endpoints.resetPassword.matchFulfilled, (state, { payload }) => {
        state.resetPasswordMessage = payload.message;
        state.error = undefined;
      })
      .addMatcher(authApi.endpoints.resetPassword.matchRejected, (state, action) => {
        const error = action.error;
        const payload = action.payload;
        
        const errorMessage = 
          (payload as any)?.data ||
          (error as any).data ||
          (payload as any)?.message ||
          (error as any).data?.message ||
          (error as any).message ||
          "Failed to reset password";
        state.error = errorMessage;
        state.resetPasswordMessage = undefined;
      });
  },
});

export const { setAuth, setError, clearAuth, setInitialized, clearMessages } = slice.actions;

export const selectAuthUsername = (state: RootState) => state.auth.username;
export const selectAuthToken = (state: RootState) => state.auth.token;

export default slice.reducer;
